import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import {
  Grid,
  SpaceBetween,
  Container,
  Header,
  Button,
} from "@amzn/awsui-components-react";
import Page from "components/page";
import { FilePreview } from "components/file-preview/FilePreview";
import CreateEditGaiaobjectForm from "components/create-edit-gaiaobject-form/CreateEditGaiaobjectForm";
import TaskSuggestions from "components/task-suggestions/TaskSuggestions";
import ApiIndicator from "components/api-indicator";
import { useBreadcrumbs } from "features/breadcrumbs";
import { useGetScanjobQuery, useGetScanResultQuery } from "./endpoints";
import { useDownloadUrlQuery } from "components/documents-table/endpoints";
import { useCreateTasksMutation } from "components/task-group/endpoints";
import { useUpdateScanJobMutation } from "pages/drafts/endpoint";
import {
  useRegisterDocumentMutation,
  useUploadUrlMutation,
} from "components/create-audit-form/endpoints";
import {
  getFileNameWithoutExtension,
  toUnixEpochTime,
  normalizeDateFormat,
} from "utils/date-format";
import { IDraftOverviewProps } from "./definitions";
import Help from "./Help";
import styles from "./style.module.scss";
import PermissionBoundary from "components/permission-boundary";
import { useAppSelector } from "app/store";
import { useNotifications } from "features/notifications";
import Loader from "components/loader";
import DraftsWizard from "components/draft-wizard/DraftWizard";
import { createTasksPayloads } from "utils/taskTransformUtils";
import {
  setTaskData,
  clearTaskData,
  useGetTaskData,
} from "features/draft-wizard";
import { useAppDispatch } from "app/store";
import { useSelectedTeam } from "features/team-settings/hooks";
import { NoticeDraftWizard } from "components/notice-draft-wizard";
import { GAIAOBJECT_CATEGORY } from "utils/gaia-constants";
import { getCategoryById } from "utils/gaiaobject-utils";
import AssessmentDetailsStep from "components/assessment-details-step/AssessmentDetailsStep";
import PaymentDetailsStep from "components/payment-details-step/PaymentDetailsStep";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { createAssessmentSchema, createPaymentSchema } from "models/schemas";
import { z } from "zod";
import { useCreateAssessmentMutation } from "pages/create-assessment/endpoints";
import { useCreatePaymentMutation } from "pages/create-payment/endpoints";
import {
  registerScanDocument,
  updateScanStatus,
  createPaymentFromData,
} from "utils/scan-job-utils";

const DraftOverview: React.FC<IDraftOverviewProps> = () => {
  const { t } = useTranslation();
  const { shortId } = useParams<{ shortId: string }>();
  const { updateBreadcrumbs } = useBreadcrumbs();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  const dispatch = useAppDispatch();
  const taskData = useGetTaskData(shortId || "");
  const teamName = useAppSelector((state) => state.audits.teamName);
  const {
    isFileScanEnabled,
    isFileScanAssessmentEnabled,
    isFileScanPaymentEnabled,
  } = useSelectedTeam();
  const [isCreating, setIsCreating] = useState(false);
  const [isFilePreviewVisible, setIsFilePreviewVisible] = useState(true);

  const [createTasks] = useCreateTasksMutation();
  const [createAssessment] = useCreateAssessmentMutation();
  const [createPayment] = useCreatePaymentMutation();
  const [updateScanJobCall] = useUpdateScanJobMutation();
  const [registerDocumentCall] = useRegisterDocumentMutation();
  const [uploadUrlCall] = useUploadUrlMutation();

  const { control, setValue } = useForm<z.infer<typeof createAssessmentSchema>>(
    {
      mode: "onChange",
      resolver: zodResolver(createAssessmentSchema),
    }
  );

  const { control: paymentControl, setValue: paymentSetValue } = useForm<
    z.infer<typeof createPaymentSchema>
  >({
    mode: "onChange",
    resolver: zodResolver(createPaymentSchema),
  });

  const {
    data: scanJobData,
    error: scanJobError,
    isLoading: scanJobLoading,
    isError: scanJobIsError,
  } = useGetScanjobQuery(
    { shortId: shortId as string },
    { refetchOnMountOrArgChange: true, skip: !shortId }
  );

  const { data: scanResultData } = useGetScanResultQuery(
    { shortId: shortId! },
    { refetchOnMountOrArgChange: true, skip: !shortId }
  );

  const {
    data: downloadUrlData,
    error: downloadUrlError,
    isLoading: downloadUrlLoading,
    isError: downloadUrlIsError,
  } = useDownloadUrlQuery(
    { documentId: shortId as string, preview: true },
    {
      skip: !shortId,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: false,
      refetchOnReconnect: false,
      pollingInterval: 0,
    }
  );
  const category = scanJobData?.scanJob?.category?.toLowerCase();

  useEffect(() => {
    updateBreadcrumbs &&
      updateBreadcrumbs([
        { text: "Gaia", href: "/" },
        { text: "Drafts", href: "/drafts" },
        { text: `${shortId || "..."}`, href: "#" },
      ]);
  }, [shortId]);

  useEffect(() => {
    if (
      !scanJobIsError &&
      scanJobData?.scanJob &&
      scanJobData.scanJob.status !== "Ready for review"
    ) {
      addNotification({
        type: "info",
        header: "Review is already complete",
        content: `This draft cannot be edited because its status is "${scanJobData.scanJob.status}".`,
        dismissible: true,
        timeout: 5000,
      });
      navigate("/drafts");
    }
  }, [scanJobData?.scanJob?.status, scanJobIsError]);

  if (scanJobLoading) {
    return <Loader loadingText="Loading draft..." />;
  }

  if (!scanJobIsError && scanJobData?.scanJob) {
    if (category === "audit" && isFileScanEnabled) {
      return <DraftsWizard category={category as GAIAOBJECT_CATEGORY} />;
    }

    if (isFileScanAssessmentEnabled && category !== "assessment") {
      return <NoticeDraftWizard category={category as GAIAOBJECT_CATEGORY} />;
    }

    if (isFileScanPaymentEnabled && category !== "payment") {
      return <NoticeDraftWizard category={category as GAIAOBJECT_CATEGORY} />;
    }
  }
  const isLoading = downloadUrlLoading;
  const isError = scanJobIsError || downloadUrlIsError;
  const error = scanJobError || downloadUrlError;
  const gaiaObjectId = scanJobData?.scanJob?.gaiaObjectId;

  const handleError = (header: string, content: string) => {
    addNotification({
      type: "error",
      header,
      content,
      dismissible: true,
      timeout: 10000,
    });
  };

  const handleSuccess = (header: string, storageKey?: string) => {
    if (storageKey) localStorage.removeItem(storageKey);
    addNotification({
      type: "success",
      header,
      dismissible: true,
      timeout: 10000,
    });
    navigate("/drafts");
  };

  const handleCreateTasks = async () => {
    if (isCreating || !taskData?.length) {
      if (!taskData?.length)
        handleError(
          "No tasks to create",
          "Please add some tasks before creating."
        );
      return;
    }
    if (!gaiaObjectId) {
      handleError(
        "Missing Gaia object ID",
        "Cannot create tasks without a valid Gaia object ID."
      );
      return;
    }

    setIsCreating(true);
    const payloads = createTasksPayloads(
      taskData,
      gaiaObjectId,
      teamName,
      scanJobData?.scanJob?.createdBy
    );

    try {
      for (const payload of payloads) {
        await createTasks(payload).unwrap();
      }
    } catch (e: any) {
      handleError(
        "Failed to create tasks",
        e?.data?.message || "An error occurred while creating tasks."
      );
      setIsCreating(false);
      return;
    }

    // Upload and register document
    if (scanJobData?.scanJob?.fileName && downloadUrlData?.url) {
      try {
        const uploadResponse = await uploadUrlCall({
          prefix: `audits/${gaiaObjectId}`,
          fileName: scanJobData.scanJob.fileName,
          teamName,
          securityLevel: "Normal",
        }).unwrap();

        const fileBlob = await fetch(downloadUrlData.url).then((r) => r.blob());
        await fetch(uploadResponse.url, {
          method: "PUT",
          body: fileBlob,
          headers: {
            "x-amz-expected-bucket-owner": uploadResponse.bucketOwner,
          },
        });

        await registerDocumentCall({
          parentId: gaiaObjectId,
          teamName,
          fileName: getFileNameWithoutExtension(scanJobData.scanJob.fileName),
          documentType: "Audit notices",
          securityLevel: "Normal",
          s3Prefix: `audits/${gaiaObjectId}/${uploadResponse.uniqueKey}/${scanJobData.scanJob.fileName}`,
          extentionType: scanJobData.scanJob.extensionType,
          auditId: gaiaObjectId,
          size: scanJobData.scanJob.size,
        }).unwrap();
      } catch (e) {
        console.error("Failed to register document:", e);
      }
    }

    try {
      await updateScanJobCall({
        shortId: shortId as string,
        gaiaObjectId,
        status: "Review complete",
      }).unwrap();
      dispatch(clearTaskData({ scanJobId: shortId || "" }));
      handleSuccess("Tasks created successfully");
    } catch (e: any) {
      handleError(
        "Failed to update draft status",
        e?.data?.message || "Tasks created but failed to update draft status."
      );
      setIsCreating(false);
    }
  };

  const handleCreateAssessment = async () => {
    if (isCreating) return;
    if (!gaiaObjectId) {
      handleError(
        "Missing Gaia object ID",
        "Cannot create assessment without a valid Gaia object ID."
      );
      return;
    }
    setIsCreating(true);
    const storageKey = `assessment-step-${shortId}`;

    try {
      const assessmentDataStr = localStorage.getItem(storageKey);
      if (!assessmentDataStr) {
        handleError(
          "No assessment data",
          "Please fill in the assessment details before creating."
        );
        setIsCreating(false);
        return;
      }

      const {
        type,
        dateReceived,
        notes,
        amounts,
        parsedTaxYearBreakdowns,
        parsedTaxYearAreaBreakdowns,
      } = JSON.parse(assessmentDataStr);

      await createAssessment({
        gaiaObjectId,
        type: type || "Preliminary",
        dateReceived: dateReceived
          ? toUnixEpochTime(normalizeDateFormat(dateReceived))
          : Math.floor(Date.now() / 1000),
        totalTaxAmount: amounts?.totalTaxAmount || "0",
        totalPenaltyAmount: amounts?.totalPenaltyAmount || "0",
        totalInterestAmount: amounts?.totalInterestAmount || "0",
        totalOtherAmount: amounts?.totalOtherAmount || "0",
        notes: notes || "",
        taxAreaBreakdown: [],
        taxYearBreakdown: (parsedTaxYearBreakdowns || []).map((b: any) => ({
          totalTaxAmount: b.totalTaxAmount || "0",
          totalPenaltyAmount: b.totalPenaltyAmount || "0",
          totalInterestAmount: b.totalInterestAmount || "0",
          totalOtherAmount: b.totalOtherAmount || "0",
          type: b.type,
          category: b.category,
        })),
        taxYearAreaBreakdown: (parsedTaxYearAreaBreakdowns || []).map(
          (b: any) => ({
            totalTaxAmount: b.totalTaxAmount || "0",
            totalPenaltyAmount: b.totalPenaltyAmount || "0",
            totalInterestAmount: b.totalInterestAmount || "0",
            totalOtherAmount: b.totalOtherAmount || "0",
            additionalInformation: b.additionalInformation || "",
            type: b.type,
            category: `${b.taxYear || ""}${b.taxAreaId || ""}`,
          })
        ),
      }).unwrap();

      await registerScanDocument(
        shortId,
        scanJobData,
        gaiaObjectId,
        teamName,
        registerDocumentCall
      );
      await updateScanStatus(shortId, gaiaObjectId, updateScanJobCall);
      handleSuccess("Assessment created successfully", storageKey);
    } catch (e: any) {
      handleError(
        "Failed to create assessment",
        e?.data?.message || "An error occurred while creating the assessment."
      );
      setIsCreating(false);
    }
  };

  const handleCreatePayment = async () => {
    if (isCreating) return;
    if (!gaiaObjectId) {
      handleError(
        "Missing Gaia object ID",
        "Cannot create payment without a valid Gaia object ID."
      );
      return;
    }
    setIsCreating(true);
    const paymentStorageKey = `payment-step-${shortId}`;

    try {
      const result = await createPaymentFromData(
        gaiaObjectId,
        paymentStorageKey,
        shortId,
        false,
        createPayment
      );

      if (!result.success && result.error) {
        handleError(
          "Failed to create payment",
          result.error || "An error occurred while creating the payment."
        );
        setIsCreating(false);
        return;
      }

      await registerScanDocument(
        shortId,
        scanJobData,
        gaiaObjectId,
        teamName,
        registerDocumentCall
      );
      await updateScanStatus(shortId, gaiaObjectId, updateScanJobCall);
      handleSuccess("Payment created successfully", paymentStorageKey);
    } catch (e: any) {
      handleError(
        "Failed to create payment",
        e?.data?.message || "An error occurred while creating the payment."
      );
      setIsCreating(false);
    }
  };

  const renderFormComponent = () => {
    if (category === "task group") {
      return (
        <TaskSuggestions
          shortId={shortId}
          auditData={scanJobData}
          teamName={teamName}
          templateId={scanResultData?.scanTaskResult?.template}
          onTaskDataChange={(newTaskData) =>
            dispatch(
              setTaskData({ scanJobId: shortId || "", taskData: newTaskData })
            )
          }
          initialTaskData={taskData}
          category={category as GAIAOBJECT_CATEGORY}
        />
      );
    }
    if (category === "payment" && isFileScanPaymentEnabled) {
      return (
        <Container header={<Header variant="h2">Payment information</Header>}>
          <PaymentDetailsStep
            key={`payment-${shortId}`}
            shortIdForScanJob={shortId}
            isScanWizard={true}
            currencySign="€"
            category={
              getCategoryById(gaiaObjectId || "") as GAIAOBJECT_CATEGORY
            }
            control={paymentControl}
            setValue={paymentSetValue}
          />
        </Container>
      );
    }

    if (category === "assessment") {
      return (
        <Container
          header={<Header variant="h2">Assessment information</Header>}
        >
          <AssessmentDetailsStep
            key={`assessment-${shortId}`}
            shortIdForScanJob={shortId}
            isScanWizard={true}
            currencySign="$"
            category={
              getCategoryById(gaiaObjectId || "") as GAIAOBJECT_CATEGORY
            }
            control={control}
            setValue={setValue}
          />
        </Container>
      );
    }
    return (
      <CreateEditGaiaobjectForm type="notice" mode="draft" shortId={shortId} />
    );
  };

  const isActionableCategory =
    category === "task group" ||
    category === "assessment" ||
    category === "payment";
  const handlePrimaryAction =
    category === "payment" && isFileScanPaymentEnabled
      ? handleCreatePayment
      : category === "assessment"
      ? handleCreateAssessment
      : handleCreateTasks;

  const pageActions = isActionableCategory ? (
    <SpaceBetween direction="horizontal" size="xs">
      <Button
        formAction="none"
        onClick={() => navigate("/drafts")}
        variant="link"
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handlePrimaryAction}
        disabled={isCreating}
      >
        {isCreating ? "Creating..." : "Create"}
      </Button>
    </SpaceBetween>
  ) : null;

  return (
    <PermissionBoundary allowMemberOnly={true}>
      <ApiIndicator
        isLoading={isLoading}
        isError={isError}
        isAlertError={true}
        error={error}
      />
      <Page
        type="page"
        title={t("pages.draft_overview.title")}
        hasBreadcrumbs={true}
        hasInfo={false}
        help={<Help />}
        className={styles.draftOverview}
      >
        <SpaceBetween direction="vertical" size="xl">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              iconName={isFilePreviewVisible ? "view-horizontal" : "view-full"}
              onClick={() => setIsFilePreviewVisible(!isFilePreviewVisible)}
            >
              {isFilePreviewVisible ? "Hide" : "Show"} File Preview
            </Button>
          </SpaceBetween>
          <Grid
            gridDefinition={
              isFilePreviewVisible
                ? [{ colspan: 6 }, { colspan: 6 }]
                : [{ colspan: 12 }]
            }
          >
            {isFilePreviewVisible && (
              <Container header={<Header variant="h2">File Preview</Header>}>
                {scanJobData?.scanJob ? (
                  <FilePreview
                    type="pdf"
                    fileName={scanJobData.scanJob.fileName}
                    presignedUrl={downloadUrlData?.url || ""}
                  />
                ) : (
                  <div>No file available for preview</div>
                )}
              </Container>
            )}
            {category === "task group" ? (
              <Container>{renderFormComponent()}</Container>
            ) : (
              renderFormComponent()
            )}
          </Grid>
          {pageActions && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {pageActions}
            </div>
          )}
        </SpaceBetween>
      </Page>
    </PermissionBoundary>
  );
};

export default DraftOverview;
